export function normalizeHex(hex: string): string {
  const raw = hex.replace('#', '');
  if (raw.length === 3) {
    return '#' + raw[0] + raw[0] + raw[1] + raw[1] + raw[2] + raw[2];
  }
  return '#' + raw.slice(0, 6);
}

export interface ColorShift {
  hueShift?: number;
  chromaShift?: number;
  lightnessShift?: number;
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const norm = normalizeHex(hex).replace('#', '');
  return {
    r: parseInt(norm.slice(0, 2), 16),
    g: parseInt(norm.slice(2, 4), 16),
    b: parseInt(norm.slice(4, 6), 16),
  };
}

export function rgbaFromHex(hex: string, alpha: number = 1): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

function clampChannel(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function rgbToHsl(rgb: { r: number; g: number; b: number }): { h: number; s: number; l: number } {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const delta = max - min;

  if (delta === 0) {
    return { h: 0, s: 0, l: l * 100 };
  }

  const s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let h = 0;
  switch (max) {
    case r:
      h = (g - b) / delta + (g < b ? 6 : 0);
      break;
    case g:
      h = (b - r) / delta + 2;
      break;
    default:
      h = (r - g) / delta + 4;
      break;
  }

  return {
    h: (h * 60) % 360,
    s: s * 100,
    l: l * 100,
  };
}

export function hslToRgb(hsl: { h: number; s: number; l: number }): { r: number; g: number; b: number } {
  const h = ((hsl.h % 360) + 360) % 360 / 360;
  const s = clampChannel(hsl.s, 0, 100) / 100;
  const l = clampChannel(hsl.l, 0, 100) / 100;

  if (s === 0) {
    const gray = Math.round(l * 255);
    return { r: gray, g: gray, b: gray };
  }

  const hueToRgb = (p: number, q: number, t: number): number => {
    let amount = t;
    if (amount < 0) amount += 1;
    if (amount > 1) amount -= 1;
    if (amount < 1 / 6) return p + (q - p) * 6 * amount;
    if (amount < 1 / 2) return q;
    if (amount < 2 / 3) return p + (q - p) * (2 / 3 - amount) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  return {
    r: Math.round(hueToRgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hueToRgb(p, q, h) * 255),
    b: Math.round(hueToRgb(p, q, h - 1 / 3) * 255),
  };
}

export function isNeutralColorShift(shift: ColorShift | null | undefined): boolean {
  return !shift
    || (!shift.hueShift && !shift.chromaShift && !shift.lightnessShift);
}

export function shiftRgbColor(
  rgb: { r: number; g: number; b: number },
  shift: ColorShift | null | undefined,
): { r: number; g: number; b: number } {
  if (isNeutralColorShift(shift)) {
    return rgb;
  }

  const hsl = rgbToHsl(rgb);
  return hslToRgb({
    h: hsl.h + (shift?.hueShift || 0),
    s: clampChannel(hsl.s + (shift?.chromaShift || 0), 0, 100),
    l: clampChannel(hsl.l + (shift?.lightnessShift || 0), 0, 100),
  });
}

export function shiftHexColor(hex: string, shift: ColorShift | null | undefined): string {
  const shifted = shiftRgbColor(hexToRgb(hex), shift);
  const toHex = (value: number) => value.toString(16).padStart(2, '0');
  return `#${toHex(shifted.r)}${toHex(shifted.g)}${toHex(shifted.b)}`;
}
